import { motion } from "framer-motion";
import { MousePointerClick, Calculator, LineChart, ArrowRight } from "lucide-react";

const STEPS = [
  {
    k: "01",
    icon: MousePointerClick,
    title: "Scegli la partita",
    desc: "Seleziona il campionato e il match che vuoi analizzare.",
  },
  {
    k: "02",
    icon: Calculator,
    title: "Inserisci le quote",
    desc: "Inserisci le quote richieste nell'interfaccia e avvia l'elaborazione.",
  },
  {
    k: "03",
    icon: LineChart,
    title: "Leggi l'analisi",
    desc: "Consulta probabilità, indicatori e movimenti elaborati da ZeroSignals in un'unica schermata.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="come-funziona"
      data-testid="how-it-works-section"
      className="relative border-t border-brand-gold/20 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12"
        >
          <div className="lg:col-span-7">
            <div className="mb-5 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-brand-neon-blue">
              <span className="h-px w-4 bg-brand-gold" />
              Come Funziona
            </div>
            <h2 className="font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-5xl text-white">
              Dal match all&apos;analisi. <br />
              <span className="text-gradient-gold">In pochi passaggi.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="max-w-md text-base leading-relaxed text-white/60 sm:text-lg">
              ZeroSignals concentra il processo di analisi in un&apos;unica
              interfaccia, riducendo il tempo necessario per raccogliere e
              interpretare le informazioni.
            </p>
          </div>
        </motion.div>

        {/* STEPS */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.k}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                data-testid={`step-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-7 transition-all hover:border-brand-gold/40 hover:bg-[#161616]"
              >
                {/* number backdrop */}
                <div className="pointer-events-none absolute -right-2 -top-6 font-display text-[110px] font-black leading-none tracking-tighter text-white/[0.035] select-none">
                  {s.k}
                </div>

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="grid h-12 w-12 place-items-center rounded-xl border border-brand-neon-blue/30 bg-brand-neon-blue/[0.06] text-brand-neon-blue transition-all duration-300 group-hover:scale-110 group-hover:border-brand-gold/60 group-hover:text-brand-gold group-hover:shadow-[0_0_24px_-6px_rgba(212,175,55,0.7)]">
                      <Icon size={20} strokeWidth={1.6} />
                    </div>
                    <span className="font-display text-[10px] uppercase tracking-[0.24em] text-brand-gold/80">
                      Step {s.k}
                    </span>
                  </div>

                  <h3 className="mt-6 font-display text-xl font-bold leading-tight tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {s.desc}
                  </p>

                  <div className="mt-6 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-brand-gold via-brand-neon-blue to-transparent transition-transform duration-500 group-hover:scale-x-100" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 flex justify-center"
        >
          <a
            href="#the-hub"
            data-testid="how-it-works-cta"
            className="group relative inline-flex items-center gap-2 rounded-full border-2 border-brand-gold bg-brand-gold px-7 py-3.5 text-sm font-bold uppercase tracking-[0.18em] text-black transition-all hover:bg-brand-gold-hover hover:shadow-[0_0_45px_-5px_rgba(0,240,255,0.7)]"
          >
            Scopri i piani
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
